'use client';

import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronUp,
  CircleDashed,
  Loader2,
  MinusCircle,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import type {
  AgentGenerationStep,
  AgentStepCopy,
  AgentStepStatus,
} from '@/lib/agent-kit/types';

/**
 * Agent kit — the shared progress checklist.
 *
 * Every agentic flow (outreach, resume improve / refine / analyze) renders this
 * same component, so progress looks and behaves identically wherever it appears.
 *
 * It is a checklist rather than a log: because each flow's step list is
 * declarative, every row renders from the start and fills in as work completes.
 * A SKIPPED row says so explicitly — a missing row reads as a failure, an
 * explicit "nothing needed doing" reads as a pass.
 *
 * Deliberately NOT a reuse of ReportGenerationProgressLog: that component builds
 * report-shaped events (companies found, contacts found, jobs matched) out of
 * step output, and none of those shapes exist in these flows. It shares this
 * one's step record contract; its rendering serves a different domain.
 */

const STATUS_ICON: Record<AgentStepStatus, ReactNode> = {
  PENDING: <CircleDashed className="h-3.5 w-3.5 text-muted-foreground/50" />,
  RUNNING: <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />,
  COMPLETED: <Check className="h-3.5 w-3.5 text-primary" />,
  SKIPPED: <MinusCircle className="h-3.5 w-3.5 text-muted-foreground/60" />,
  FAILED: <AlertTriangle className="h-3.5 w-3.5 text-destructive" />,
};

export interface AgentProgressProps<TKey extends string> {
  steps: AgentGenerationStep<TKey>[];
  copy: AgentStepCopy<TKey>;
  status: 'idle' | 'running' | 'ready' | 'error';
  error?: string | null;
  /** Heading shown once the run finishes. */
  doneTitle?: string;
  /**
   * One-line summary per completed step. Each flow reads its own output shape,
   * so this stays a callback rather than being generalised into the kit.
   */
  summarize?: (step: AgentGenerationStep<TKey>) => string | null;
  /** Reasoning panel revealed by the "Why" toggle once the run is done. */
  detail?: ReactNode;
  /** When true, the step list collapses once the run finishes (status === 'ready'). */
  collapsible?: boolean;
  className?: string;
}

export function AgentProgress<TKey extends string>({
  steps,
  copy,
  status,
  error,
  doneTitle = 'Here is what happened',
  summarize,
  detail,
  collapsible = false,
  className,
}: AgentProgressProps<TKey>) {
  const [showDetail, setShowDetail] = useState(false);
  const [stepsExpanded, setStepsExpanded] = useState(true);

  const isCollapsed = collapsible && status === 'ready' && !stepsExpanded;

  if (status === 'idle') return null;

  const runningStep = steps.find((step) => step.status === 'RUNNING');
  const heading =
    status === 'error'
      ? 'Something went wrong'
      : status === 'ready'
        ? doneTitle
        : runningStep
          ? copy.labels[runningStep.step_key]
          : 'Getting started';

  return (
    <div className={cn('border border-border/70 bg-muted/20 p-3', className)}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          {collapsible && status === 'ready' ? (
            <button
              type="button"
              onClick={() => setStepsExpanded((v) => !v)}
              className="flex items-center gap-1.5 text-label text-foreground hover:text-foreground/80"
            >
              {heading}
              <ChevronDown className={cn('h-3.5 w-3.5 text-muted-foreground transition-transform', stepsExpanded && 'rotate-180')} />
            </button>
          ) : (
            <p className="text-label text-foreground">{heading}</p>
          )}
          {status === 'running' && runningStep ? (
            <p className="text-caption text-muted-foreground">
              {copy.descriptions[runningStep.step_key]}
            </p>
          ) : null}
          {status === 'error' && error ? (
            <p className="text-caption text-destructive">{error}</p>
          ) : null}
        </div>

        {status === 'ready' && detail && stepsExpanded ? (
          <button
            type="button"
            onClick={() => setShowDetail((open) => !open)}
            className="flex shrink-0 items-center gap-1 text-caption text-muted-foreground hover:text-foreground"
          >
            {showDetail ? 'Hide' : 'Why'}
            {showDetail ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
          </button>
        ) : null}
      </div>

      <AnimatePresence initial={false}>
        {!isCollapsed ? (
          <motion.div
            initial={false}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <ul className="mt-2 space-y-0">
              {steps.map((step) => {
                const summary = summarize?.(step) ?? null;
                const isInactive = step.status === 'PENDING';
                return (
                  <li key={step.step_key} className="flex items-start gap-2.5 py-1">
                    <span className="mt-0.5 shrink-0">{STATUS_ICON[step.status]}</span>
                    <span className="min-w-0 flex-1">
                      <span
                        className={cn(
                          'block text-label',
                          isInactive ? 'text-muted-foreground/60' : 'text-foreground',
                          step.status === 'FAILED' && 'text-destructive',
                        )}
                      >
                        {copy.labels[step.step_key]}
                      </span>
                      {step.status === 'FAILED' && step.error_message ? (
                        <span className="block text-caption text-destructive">
                          {step.error_message}
                        </span>
                      ) : summary ? (
                        <span className="block text-caption text-muted-foreground">{summary}</span>
                      ) : null}
                    </span>
                  </li>
                );
              })}
            </ul>

            {showDetail && detail ? (
              <div className="mt-3 space-y-3 border-t border-border/60 pt-3">{detail}</div>
            ) : null}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
